import { BigNumber } from "@yjsgames/bignumber";
import { t } from "../i18n/i18n.js";
import { formatBig } from "./formatNumber.js";
import { MODULE_BRICK_BALANCE, MIN_FIRE_INTERVAL_MS } from "../data/balance.js";
import {
  composeBaseDamage,
  composeFireIntervalMs,
  composeGoldMultiplier,
  composeCritChance,
  composeCritMultiplier,
} from "../systems/statComposition.js";
import { resolvePurchase, type Multiplier } from "../systems/multiBuy.js";
import type { StatUpgradeController } from "../systems/StatUpgradeController.js";
import type { RebirthController } from "../systems/RebirthController.js";
import type { ChapterController } from "../systems/ChapterController.js";
import type { RelicController } from "../systems/RelicController.js";
import type { EconomyController } from "../systems/EconomyController.js";
import type { RebirthTabData } from "./RebirthTabView.js";
import type { StatUpgradeData } from "./StatUpgradeTabView.js";

/**
 * 스탯/환생 탭 데이터 빌더 — tabData.ts에서 분리(god-file 축소, 2026-06-09).
 * 두 탭이 (스탯 레벨 + 환생 레벨 + 유물) 합성 cell 표기를 공유하므로 composeStatCell을 공용으로 둔다.
 * 합성은 statComposition 순수 함수 재사용 → 현재값이 실제 게임값(GameScene.getBaseDamage/currentFireIntervalMs)과 동일 입력.
 * 호출부(GameScene) 호환을 위해 tabData.ts가 buildRebirthData/buildStatUpgradeData를 re-export한다.
 * `t()`/`formatBig()`(i18n·표기 형식 전역)만 모듈에서 직접 사용한다(read-only 순수, side effect 없음).
 */

/** cell 3줄(제목+레벨 / 소스 분해 / 현재→다음 예상값) 표시 문자열. */
interface StatCellStrings {
  readonly cellName: string;
  readonly breakdown: string;
  readonly result: string;
}

/**
 * 합성 스탯 3종(attack/fireRate/goldGain)의 cell 표시 문자열 — 스탯/환생 탭 공용.
 * 강화 대상(targetIsRebirth: false=골드 스탯, true=환생 스탯)의 현재 레벨→nextLevel 변화만 다르고,
 * 세 소스(스탯·환생·유물) 합성식·분해 표기는 동일하다. 합성은 statComposition 순수 함수를 재사용해
 * 현재값이 실제 게임값(GameScene.getBaseDamage/currentFireIntervalMs)과 동일 입력이 되게 한다.
 * i18n 키는 statCell.* (양 탭 공유), 행 이름 라벨만 nameKeyPrefix(statUpgrade.stat./rebirth.stat.)로 분기.
 */
function composeStatCell(
  statId: "attack" | "fireRate" | "goldGain" | "critChance" | "critDamage",
  statUpgrade: StatUpgradeController,
  rebirthUpgrade: StatUpgradeController,
  relic: RelicController,
  nextLevel: number,
  targetIsRebirth: boolean,
  nameKeyPrefix: string,
): StatCellStrings {
  const su = statUpgrade;
  const ru = rebirthUpgrade;
  // cellName 레벨 = 강화 대상(스탯 탭=골드 스탯 / 환생 탭=환생 스탯)의 현재 레벨.
  const targetLevel = (targetIsRebirth ? ru : su).level(statId);
  // 표기는 Lv.1부터 — 내부 레벨 0(미강화)을 사용자에게 "Lv.1"로 보인다(내부 계산은 실제 레벨 유지).
  const cellName = t("statCell.name", { name: t(nameKeyPrefix + statId), level: targetLevel + 1 });
  // 골드 스탯/환생 각각의 현재 효과(현재 레벨 직접 조회). 다음값은 강화 대상(targetIsRebirth)만 nextLevel을 적용.
  const statCur = su.effectAt(statId, su.level(statId));
  const rebCur = ru.effectAt(statId, ru.level(statId));
  const statNext = targetIsRebirth ? statCur : su.effectAt(statId, nextLevel);
  const rebNext = targetIsRebirth ? ru.effectAt(statId, nextLevel) : rebCur;
  const B = MODULE_BRICK_BALANCE;

  switch (statId) {
    case "attack": {
      const relicMult = relic.attackMultiplier();
      const cur = composeBaseDamage(B.baseDamage, rebCur, statCur, relicMult);
      const next = composeBaseDamage(B.baseDamage, rebNext, statNext, relicMult);
      return {
        cellName,
        breakdown: t("statCell.breakdown.attack", {
          stat: Math.round(statCur),
          rebirth: Math.round(rebCur),
          relic: relicMult.toFixed(2),
        }),
        result: t("statCell.result.attack", {
          current: formatBig(BigNumber.fromNumber(Math.round(cur))),
          next: formatBig(BigNumber.fromNumber(Math.round(next))),
        }),
      };
    }
    case "fireRate": {
      const relicMult = relic.fireRateMultiplier();
      const cur = composeFireIntervalMs(B.fireIntervalMs, statCur, rebCur, relicMult, MIN_FIRE_INTERVAL_MS);
      const next = composeFireIntervalMs(B.fireIntervalMs, statNext, rebNext, relicMult, MIN_FIRE_INTERVAL_MS);
      return {
        cellName,
        breakdown: t("statCell.breakdown.fireRate", {
          stat: Math.round(statCur),
          rebirth: Math.round(rebCur),
          relic: relicMult.toFixed(2),
        }),
        result: t("statCell.result.fireRate", {
          current: Math.round(cur),
          next: Math.round(next),
        }),
      };
    }
    case "goldGain": {
      const relicMult = relic.goldMultiplier();
      // goldGain effect는 "1 + effect" 배수로 환산 (StatUpgradeController.goldMultiplier()와 동일 의미).
      const cur = composeGoldMultiplier(1 + statCur, 1 + rebCur, relicMult, 1);
      const next = composeGoldMultiplier(1 + statNext, 1 + rebNext, relicMult, 1);
      return {
        cellName,
        breakdown: t("statCell.breakdown.goldGain", {
          stat: (1 + statCur).toFixed(2),
          rebirth: (1 + rebCur).toFixed(2),
          relic: relicMult.toFixed(2),
        }),
        result: t("statCell.result.goldGain", {
          current: cur.toFixed(2),
          next: next.toFixed(2),
        }),
      };
    }
    case "critChance": {
      // 유물엔 crit 효과 없음 — 스탯+환생만 합성(모듈 critChance는 발사체별이라 cell 미표시). 0~1을 %로 표기.
      const cur = composeCritChance(0, statCur, rebCur);
      const next = composeCritChance(0, statNext, rebNext);
      return {
        cellName,
        breakdown: t("statCell.breakdown.critChance", {
          stat: (statCur * 100).toFixed(1),
          rebirth: (rebCur * 100).toFixed(1),
        }),
        result: t("statCell.result.critChance", {
          current: (cur * 100).toFixed(1),
          next: (next * 100).toFixed(1),
        }),
      };
    }
    case "critDamage": {
      // 유물·모듈 미합성 표시 — 기본 2배(critMultiplier) 바닥값 + 스탯/환생 가산(모듈 critMul=1 전달).
      // 모듈 등급 상승분(legendary 3 등)은 발사체 spec에서만 합류 — 스탯 탭은 영구 강화분만 보여준다.
      const cur = composeCritMultiplier(B.critMultiplier, 1, statCur, rebCur);
      const next = composeCritMultiplier(B.critMultiplier, 1, statNext, rebNext);
      return {
        cellName,
        breakdown: t("statCell.breakdown.critDamage", {
          stat: statCur.toFixed(1),
          rebirth: rebCur.toFixed(1),
        }),
        result: t("statCell.result.critDamage", {
          current: cur.toFixed(1),
          next: next.toFixed(1),
        }),
      };
    }
  }
}

/**
 * 환생 전용 pointBonus cell — 게임 스탯 합성 대상이 아니라 "환생 시 적립 포인트 가산"이라
 * 소스 분해(스탯·유물) 없이 현재→다음(레벨 +1) 적립 보너스만 표시한다.
 */
function composePointBonusCell(
  rebirthUpgrade: StatUpgradeController,
  level: number,
  nextLevel: number,
  maxed: boolean,
): StatCellStrings {
  const cur = rebirthUpgrade.effectOf("pointBonus");
  const next = maxed ? cur : rebirthUpgrade.effectAt("pointBonus", nextLevel);
  return {
    // 표기 Lv.1부터 (composeStatCell과 일관) — 내부 level 0 = 화면 "Lv.1".
    cellName: t("statCell.name", { name: t("rebirth.stat.pointBonus"), level: level + 1 }),
    breakdown: "",
    result: t("statCell.result.pointBonus", {
      current: Math.round(cur),
      next: Math.round(next),
    }),
  };
}

function formatRebirthPointFormula(expr: string): string {
  return expr
    .replace(/\{bestChapter\}/g, t("rebirth.formula.bestChapter"))
    .replace(/\*\*/g, "^")
    .replace(/\s+/g, "");
}

/**
 * 환생 탭 — 강화 4종(제목+레벨 / 소스 분해(스탯·환생·유물) / 현재→다음 예상 최종값 + 환생포인트 비용) +
 * 환생 정보(챕터·포인트·조건·획득예상). 합성 스탯(attack/fireRate/goldGain)은 골드 스탯(statUpgrade)·
 * 유물(relic)과 합성하며 다음값은 환생 레벨 +1 기준. pointBonus는 환생 단독.
 */
export function buildRebirthData(
  rebirth: RebirthController,
  rebirthUpgrade: StatUpgradeController,
  chapter: ChapterController,
  statUpgrade: StatUpgradeController,
  relic: RelicController,
  mult: Multiplier = 1,
): RebirthTabData {
  const ru = rebirthUpgrade;
  const pt = rebirth.rebirthPoint;
  const pointBalance = BigNumber.fromNumber(pt);
  const pointBreakdown = rebirth.gainedPointBreakdown(
    chapter.bestChapter,
    ru.effectOf("pointBonus"),
  );
  const rows = ["attack", "fireRate", "goldGain", "critChance", "critDamage", "pointBonus"].map(
    (statId) => {
      const maxed = ru.isMaxed(statId);
      const level = ru.level(statId);
      const { count, totalCost } = resolvePurchase(
        (lv) => ru.unitCostAt(statId, lv),
        level,
        ru.maxLevelOf(statId),
        pointBalance,
        mult,
      );
      const displayCount = Math.max(1, count);
      const cost = count > 0 ? totalCost : ru.cost(statId);
      const cell =
        statId === "pointBonus"
          ? composePointBonusCell(ru, level, level + displayCount, maxed)
          : composeStatCell(
              statId as "attack" | "fireRate" | "goldGain" | "critChance" | "critDamage",
              statUpgrade,
              ru,
              relic,
              maxed ? level : level + displayCount,
              true,
              "rebirth.stat.",
            );
      return {
        statId,
        level,
        cellName: cell.cellName,
        breakdown: cell.breakdown,
        result: cell.result,
        cost,
        count,
        affordable: !maxed && count > 0 && pointBalance.gte(cost), // 환생 포인트로 강화 가능
        maxed,
      };
    },
  );
  return {
    rebirthPoint: pt,
    requiredChapter: rebirth.requiredChapter(),
    gainedIfRebirth: pointBreakdown.totalPoints,
    gainedBasePoints: pointBreakdown.basePoints,
    gainedBonusPoints: pointBreakdown.bonusPoints,
    pointFormula: formatRebirthPointFormula(MODULE_BRICK_BALANCE.rebirthPointExpr),
    canRebirth: rebirth.canRebirth(chapter.currentChapter),
    rows,
  };
}

/**
 * 스탯 탭 — 3종 스탯의 cell(제목+레벨 / 소스 분해(스탯·환생·유물) / 현재→다음 예상 최종값) +
 * 선택 배수 기준 총비용/구매 가능. 합성은 골드 스탯(statUpgrade)·환생(rebirthUpgrade)·유물(relic)을 묶어
 * composeStatCell이 수행하며, 다음값은 선택 배수(count, 최소 1레벨)만큼 강화한 골드 스탯 기준이다.
 */
export function buildStatUpgradeData(
  statUpgrade: StatUpgradeController,
  rebirthUpgrade: StatUpgradeController,
  relic: RelicController,
  economy: EconomyController,
  mult: Multiplier,
): StatUpgradeData {
  const su = statUpgrade;
  const gold = economy.gold;
  const rows: StatUpgradeData["rows"] = {};
  for (const statId of ["attack", "fireRate", "goldGain", "critChance", "critDamage"] as const) {
    const maxed = su.isMaxed(statId);
    const level = su.level(statId);
    // 현재 배수 → 구매 레벨 수 + 총비용. xMax는 보유 골드로 살 수 있는 최대.
    const { count, totalCost } = resolvePurchase(
      (lv) => su.unitCostAt(statId, lv),
      level,
      su.maxLevelOf(statId),
      gold,
      mult,
    );
    // count==0(xMax 골드 부족)이면 "필요한 다음 1레벨 비용"을 표시해 부족을 인지시킨다.
    const displayCost = count > 0 ? totalCost : su.unitCostAt(statId, level);
    // 다음 예상값 레벨 — 만렙은 현재, 그 외 최소 1레벨 앞(count=0이어도 1레벨 미리보기).
    const nextLevel = maxed ? level : level + Math.max(1, count);
    const cell = composeStatCell(
      statId,
      su,
      rebirthUpgrade,
      relic,
      nextLevel,
      false,
      "statUpgrade.stat.",
    );
    rows[statId] = {
      level,
      cellName: cell.cellName,
      breakdown: cell.breakdown,
      result: cell.result,
      cost: maxed ? BigNumber.ZERO : displayCost,
      count,
      affordable: !maxed && count > 0 && gold.gte(totalCost),
      maxed,
    };
  }
  return { rows };
}
